<template>
  <div class="data-info-modal">
    <base-right-side-modal @hide="onCloseModal" :is-modal-open="isOpen" hide-footer>
      <template #modal-header>
        <div class="header-right-side-container">
          <div>
            {{ t('bi.data_info') }}
          </div>
          <div>
            <button
              class="btn btn-export"
              @click="onClickExportDataInfo"
              v-bind:disabled="loadingExport"
              v-permission="$can('portal-cdp', 'cdp-portal-dashboard-downloadcsv')"
            >
              <div class="d-inline mr-2">
                <span v-if="loadingExport">
                  <span class="spinner-border" role="status">
                    <span class="visually-hidden">{{ t('global.loading') }}</span>
                  </span>
                </span>
                <span v-else>
                  <export-icon style="fill: #41b726" />
                </span>
              </div>
              {{ t('global.export') }}
            </button>
          </div>
        </div>
      </template>
      <template #modal-body>
        <div>
          <div>
            <strong>{{ t('bi.information') }}</strong>
          </div>
          <div class="p-3">
            <div class="my-2">
              <div class="text-black-50">{{ t('bi.chart_name') }}:</div>
              <strong>{{ `${chartName || '-'}` }}</strong>
            </div>
            <div class="my-2">
              <div class="text-black-50">{{ t('bi.description') }}:</div>
              <strong>{{ `${description || '-'}` }}</strong>
            </div>
            <div class="my-2">
              <div class="text-black-50">{{ t('bi.filters') }}:</div>
              <div v-for="(filter, index) in filteredFilters" :key="index" style="word-break: break-word">
                {{ `${resolveInfoFilterKey(filter.key)}: ${resolveInfoFilterValue(filter.value, filter.key)}` }}
              </div>
            </div>
            <div v-if="dynamicFilterList.length > 0" class="my-2">
              <div class="text-black-50">{{ t('bi.dynamicFilter') }}:</div>
              <div class="dynamic-filter-wrp">
                <div v-for="(items, groupName) in groupedFilters" :key="groupName">
                  <strong>{{ groupName }}:</strong> {{ items.join(', ') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <base-table
          class="base-table"
          :columns="columns"
          :data="data"
          :currentPage="pagination.currentPage"
          :perPages="pagination.perPage"
          responsive
        >
          <template v-for="column in columns" :key="column.key" v-slot:[renderSlotName(column.key)]="slotData">
            <template v-if="column.columnType != 2 && slotData.item[column.key] != null">
              <div class="ellipsis-cell" :ref="(el) => registerCellRef(column.key, el)">
                <EllipsisTooltip
                  :text="String(slotData.item[column.key])"
                  :limit="columnWidths[column.key] ? calculateCharLimit(columnWidths[column.key]) : 30"
                />
              </div>
            </template>
            <template v-else>
              <component :key="column.key" :is="resolveComponent(column.columnType)" :data="slotData.item[column.key]" :rawData="slotData.item" />
            </template>
          </template>
        </base-table>
        <div class="d-flex justify-content-end">
          <Pagination v-model="pagination.currentPage" :totalRows="pagination.totalRows" :perPages="pagination.perPage" />
        </div>
      </template>
    </base-right-side-modal>
  </div>
</template>

<script lang="ts" setup>
import { computed,reactive, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseRightSideModal from '@/views/components/modal/BaseRightSideModal.vue';
import BaseTable from '@/views/components/table/base-table/BaseTable.vue';
import Pagination from '@/views/components/pagination/Pagination.vue';

import ExportIcon from '@/assets/icons/modules/analytics/bi/ExportIcon.vue';
import useDataInfoModal from './hooks/useDataInfoModal';

import EllipsisTooltip from '@/views/components/ellipsis-tooltip/EllipsisTooltip.vue';
import { cp } from 'fs';

interface Props {
  isOpen: boolean;
  chartName: string;
  description: string;
  filters: DataInfoModal.Filter[];
  dataInfo: DataInfoModal.DataInfoTable;
  chartDataInfoExportProps: Charts.ChartDataInfoExportProps;
  dynamicFilter: string[];
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
});

defineEmits(['close-modal']);

const { t } = useI18n();

const {
  columns,
  data,
  pagination,
  onCloseModal,
  renderSlotName,
  resolveComponent,
  resolveInfoFilterKey,
  resolveInfoFilterValue,
  onClickExportDataInfo,
  loadingExport,
} = useDataInfoModal(props);

const dynamicFilterList = ref<String[]>([]);

const filteredFilters = computed(() => {
  return props.filters.filter((filter) => filter.key !== 'dynamicFilter');
});

const cellRefs = reactive<Record<string, HTMLElement[]>>({});
const columnWidths = reactive<Record<string, number>>({});

function registerCellRef(key: string, el: unknown) {
  const element = el as HTMLElement;
  if (element && element instanceof HTMLElement) {
    if (!cellRefs[key]) cellRefs[key] = [];
    cellRefs[key].push(element);
  }
}

function calculateCharLimit(width: number): number {
  const avgCharWidth = 7.5; // ปรับตาม font ที่ใช้จริง
  return Math.floor(width / avgCharWidth);
}

const groupedFilters = computed(() => {
  const groups: Record<string, string[]> = {};

  dynamicFilterList.value.forEach((item) => {
    const parts = item.split('_');
    const groupKey = `${parts[0]}/${parts[2]}`;
    const displayName = parts.at(-1) === '0' ? 'ทั้งหมด' : (parts.at(-1) ?? '');

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }

    groups[groupKey].push(displayName);
  });

  return groups;
});

onBeforeMount(() => {
  dynamicFilterList.value = props.dynamicFilter;
});

</script>

<style lang="scss" scoped>
@import '@assets/styles/modules/analytics/bi/analytics-data-info-modal.scss';

.ellipsis-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
</style>
